import type { SubIB as DrawerSubIB, SubIBHoid } from "../types";
import type { Hoid, SubIB as RosterSubIB } from "./data";

/**
 * Adapts a Shared-rebate-programs roster entry to the shape `SubIBDetailDrawer`
 * expects.
 *
 * Needed because the two tabs are fed by **different mock datasets** with
 * partially overlapping ids: the Sub IBs tab comes from the portal's
 * `pages/SubIBReport/mockData.ts` (subs `sub-1…sub-5`, HOIDs `44xx`), while the
 * programs tab and its partners come from `rebate-programs/data.ts` (subs
 * `sub-1…sub-11`, HOIDs `92xx…98xx`). Looking a program's partner up in the
 * portal set therefore resolved either a *different* person or nobody at all,
 * so the drawer opened blank — the design shows it populated from the same
 * dataset the program came from.
 *
 * The two `SubIB` types also differ: the roster names the reference
 * `partnerId` (the drawer calls it `subRef`) and capitalises its status/KYC
 * enums, so those are normalised here.
 */
const toDrawerHoid = (hoid: Hoid): SubIBHoid => ({
  id: hoid.id,
  rebateProgramLabel: hoid.rebateProgramLabel,
  status: hoid.status === "Archived" ? "archived" : "active",
  rebateSplitPct: hoid.rebateSplitPct,
  lastPayment: hoid.lastPayment,
  subIbPayout: hoid.subIbPayout,
  createdAt: hoid.createdAt,
  referralLink: hoid.referralLink,
  partners: hoid.partners.map((p) => ({
    subIbId: p.subIbId,
    name: p.name,
    splitPct: p.splitPct,
  })),
  totalRebate: hoid.totalRebate,
  myPayout: hoid.myPayout,
  uniqueClients: hoid.uniqueClients,
  clientAccounts: hoid.clientAccounts,
  tradingVolume: hoid.tradingVolume,
});

export const toDrawerSubIb = (sub: RosterSubIB): DrawerSubIB => ({
  id: sub.id,
  subRef: sub.partnerId,
  name: sub.name,
  email: sub.email,
  kycStatus: sub.kycStatus === "Pending" ? "pending" : "approved",
  comment: sub.comment,
  addedAt: sub.addedAt,
  hoids: sub.hoids.map(toDrawerHoid),
  aggregateStatus: sub.aggregateStatus === "Inactive" ? "inactive" : "active",
  rebateSplitMin: sub.rebateSplitMin,
  rebateSplitMax: sub.rebateSplitMax,
  lastPayment: sub.lastPayment,
  aggregateSubIbPayout: sub.aggregateSubIbPayout,
  totalRebate: sub.totalRebate,
  myPayout: sub.myPayout,
  aggregateUniqueClients: sub.aggregateUniqueClients,
  clientAccounts: sub.clientAccounts,
  tradingVolume: sub.tradingVolume,
});
